import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
    ShieldCheck, Eye, EyeOff, Check, X as XIcon, LogOut, Loader2, KeyRound, Info,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PASSWORD_INPUT_PROPS } from "@/utils/passwordInputProps";

/**
 * Security & Account — inline section rendered on the User Profile page.
 *
 * Available to every authenticated user (customer + installer). Covers:
 *   • Change Password — with reveal toggles, checklist and strength meter
 *   • Session management — "Log out of all other devices"
 *
 * The frontend enforces UX only. All password rules re-run server-side
 * (`/api/auth/change-password`) which is the authoritative gate.
 */

// Client-side hint list. Server owns the true policy (see
// core/security/policy.py). Kept in sync with the spec: min 12 chars,
// upper/lower/number/special, plus history + no name/email substrings.
const REQUIREMENTS = [
    { key: "length",  label: "At least 12 characters",           test: (p) => p.length >= 12 },
    { key: "upper",   label: "One uppercase letter",             test: (p) => /[A-Z]/.test(p) },
    { key: "lower",   label: "One lowercase letter",             test: (p) => /[a-z]/.test(p) },
    { key: "number",  label: "One number",                       test: (p) => /\d/.test(p) },
    { key: "special", label: "One special character",            test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function scoreOf(pw) {
    if (!pw) return 0;
    let s = 0;
    for (const r of REQUIREMENTS) if (r.test(pw)) s += 1;
    if (pw.length >= 16) s += 1;
    if (pw.length >= 20) s += 1;
    return Math.min(s, 5);
}

const STRENGTH_LABEL = ["", "Very weak", "Weak", "Fair", "Strong", "Excellent"];
const STRENGTH_CLASS = [
    "bg-[#E5E7EB]", "bg-[#DA3535]", "bg-[#F59E0B]", "bg-[#EAB308]", "bg-[#65A30D]", "bg-[#3E9E2E]",
];


export default function SecurityAccountSection({ testid = "security-account" }) {
    return (
        <section className="mt-8" data-testid={testid}>
            <div className="text-[11px] text-[#4B5565] tracking-[0.14em] font-medium uppercase mb-2 px-1">
                Security & account
            </div>

            <div className="pe-card p-5 md:p-6" data-testid={`${testid}-card`}>
                <div className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-full bg-[#E1E6F0] text-[#131B2F] flex items-center justify-center shrink-0">
                        <ShieldCheck size={18} strokeWidth={1.75} />
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className="text-base text-[#131B2F] font-medium">Keep your account secure</div>
                        <p className="text-sm text-[#4B5565] mt-0.5">
                            Change your password or sign out of any device you're not using right now. Only you can see this section.
                        </p>
                    </div>
                </div>

                <div className="mt-6 pt-6 border-t border-[#E5E7EB]">
                    <ChangePasswordForm testid={`${testid}-change-password`} />
                </div>

                <div className="mt-6 pt-6 border-t border-[#E5E7EB]">
                    <SessionManagement testid={`${testid}-sessions`} />
                </div>
            </div>
        </section>
    );
}


function ChangePasswordForm({ testid }) {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState({ current: false, next: false, confirm: false });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    const score = useMemo(() => scoreOf(next), [next]);
    const requirementStatus = useMemo(
        () => REQUIREMENTS.map((r) => ({ ...r, ok: r.test(next) })),
        [next],
    );
    const matchOk = confirm.length > 0 && confirm === next;

    const canSubmit = current.length > 0 &&
                      next.length >= 12 &&
                      matchOk &&
                      next !== current &&
                      !busy;

    async function onSubmit(e) {
        e.preventDefault();
        setError(""); setNotice("");
        if (!canSubmit) return;
        setBusy(true);
        try {
            const r = await api.changePassword(current, next, confirm);
            const revoked = r?.other_sessions_revoked || 0;
            const suffix = revoked > 0
                ? `Other devices signed out (${revoked}).`
                : "You remain signed in on this device.";
            setNotice(`Password updated. ${suffix}`);
            toast.success("Password updated");
            setCurrent(""); setNext(""); setConfirm("");
            setShow({ current: false, next: false, confirm: false });
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || "Failed to change password");
        } finally { setBusy(false); }
    }

    return (
        <form onSubmit={onSubmit} data-testid={testid}>
            <div className="flex items-center gap-2">
                <KeyRound size={16} strokeWidth={1.75} className="text-[#131B2F]" />
                <h3 className="text-sm font-medium text-[#131B2F]">Change password</h3>
            </div>
            <p className="mt-1 text-xs text-[#4B5565]">
                Choose something you don't use anywhere else. We'll sign you out of every other device automatically.
            </p>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <RevealField
                    label="Current password"
                    value={current}
                    onChange={setCurrent}
                    show={show.current}
                    onToggleShow={() => setShow((s) => ({ ...s, current: !s.current }))}
                    autoComplete="current-password"
                    testid={`${testid}-current`}
                />
                <div className="hidden md:block" />
                <RevealField
                    label="New password"
                    value={next}
                    onChange={setNext}
                    show={show.next}
                    onToggleShow={() => setShow((s) => ({ ...s, next: !s.next }))}
                    autoComplete="new-password"
                    testid={`${testid}-new`}
                />
                <RevealField
                    label="Confirm new password"
                    value={confirm}
                    onChange={setConfirm}
                    show={show.confirm}
                    onToggleShow={() => setShow((s) => ({ ...s, confirm: !s.confirm }))}
                    autoComplete="new-password"
                    testid={`${testid}-confirm`}
                    warning={confirm && !matchOk ? "Passwords don't match yet" : null}
                />
            </div>

            {(next.length > 0 || confirm.length > 0) && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-xs text-[#4B5565]">
                            <span className="font-medium text-[#131B2F]" data-testid={`${testid}-strength-label`}>
                                Strength: {STRENGTH_LABEL[score] || "Very weak"}
                            </span>
                        </div>
                        <div
                            className="mt-1.5 h-1.5 rounded-full bg-[#E5E7EB] overflow-hidden"
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={5}
                            aria-valuenow={score}
                            data-testid={`${testid}-strength-bar`}
                        >
                            <div
                                className={`h-full transition-all ${STRENGTH_CLASS[score] || STRENGTH_CLASS[0]}`}
                                style={{ width: `${(score / 5) * 100}%` }}
                            />
                        </div>
                        <p className="mt-2 text-[11px] text-[#4B5565] flex items-start gap-1.5">
                            <Info size={12} className="mt-0.5 shrink-0" />
                            Our server also blocks common passwords, ones based on your name or email, and any of your last 5.
                        </p>
                    </div>
                    <ul className="text-xs space-y-1" data-testid={`${testid}-requirements`}>
                        {requirementStatus.map((r) => (
                            <li
                                key={r.key}
                                className={`flex items-center gap-1.5 ${r.ok ? "text-[#2E7E22]" : "text-[#4B5565]"}`}
                                data-testid={`${testid}-req-${r.key}${r.ok ? "-ok" : ""}`}
                            >
                                {r.ok ? <Check size={13} /> : <XIcon size={13} />}
                                <span>{r.label}</span>
                            </li>
                        ))}
                        {next && current && next === current && (
                            <li className="flex items-center gap-1.5 text-[#B45309]">
                                <XIcon size={13} /> Must differ from your current password
                            </li>
                        )}
                    </ul>
                </div>
            )}

            {error && (
                <div className="mt-4 text-sm text-[#DA3535]" data-testid={`${testid}-error`}>
                    {error}
                </div>
            )}
            {notice && (
                <div className="mt-4 text-sm text-[#2E7E22] bg-[#F1F8EC] rounded-md px-3 py-2" data-testid={`${testid}-notice`}>
                    {notice}
                </div>
            )}

            <div className="mt-5 flex justify-end">
                <button
                    type="submit"
                    disabled={!canSubmit}
                    data-testid={`${testid}-submit`}
                    className="pe-btn-primary h-11 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {busy && <Loader2 size={14} className="animate-spin" />}
                    Save new password
                </button>
            </div>
        </form>
    );
}


function RevealField({ label, value, onChange, show, onToggleShow, testid, autoComplete, warning }) {
    return (
        <div>
            <label className="block text-sm text-[#4B5565] mb-1.5">{label}</label>
            <div className="relative">
                <input
                    type={show ? "text" : "password"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    autoComplete={autoComplete}
                    {...PASSWORD_INPUT_PROPS}
                    data-testid={testid}
                    className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white pl-3.5 pr-11 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E]"
                />
                <button
                    type="button"
                    onClick={onToggleShow}
                    aria-label={show ? "Hide password" : "Show password"}
                    data-testid={`${testid}-reveal`}
                    className="absolute inset-y-0 right-0 w-11 flex items-center justify-center text-[#4B5565] hover:text-[#131B2F]"
                >
                    {show ? <EyeOff size={16} strokeWidth={1.75} /> : <Eye size={16} strokeWidth={1.75} />}
                </button>
            </div>
            {warning && (
                <p className="mt-1 text-xs text-[#B45309]" data-testid={`${testid}-warning`}>{warning}</p>
            )}
        </div>
    );
}


function SessionManagement({ testid }) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState("");
    const [error, setError] = useState("");

    async function performLogoutOthers() {
        setBusy(true);
        setError(""); setNotice("");
        try {
            const r = await api.logoutOthers();
            const revoked = r?.revoked || 0;
            const line = revoked === 0
                ? "No other active sessions were found — you're only signed in here."
                : `Signed out of ${revoked} other device${revoked === 1 ? "" : "s"}. This device stays signed in.`;
            setNotice(line);
            toast.success("Other devices signed out");
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || "Couldn't sign out other devices");
        } finally {
            setBusy(false);
            setConfirmOpen(false);
        }
    }

    return (
        <div data-testid={testid}>
            <div className="flex items-center gap-2">
                <LogOut size={16} strokeWidth={1.75} className="text-[#131B2F]" />
                <h3 className="text-sm font-medium text-[#131B2F]">Session management</h3>
            </div>
            <p className="mt-1 text-xs text-[#4B5565]">
                If you've used Pearl on a shared computer or a device you no longer have, sign it out from here.
            </p>

            <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-sm text-[#4B5565]">This device stays signed in. Everywhere else gets kicked out.</div>
                <button
                    type="button"
                    onClick={() => setConfirmOpen(true)}
                    disabled={busy}
                    data-testid={`${testid}-open-confirm`}
                    className="pe-btn-outline h-11 text-sm text-[#131B2F] inline-flex items-center gap-2"
                >
                    <LogOut size={14} strokeWidth={1.75} />
                    Log out of all other devices
                </button>
            </div>

            {notice && (
                <div className="mt-3 text-sm text-[#2E7E22] bg-[#F1F8EC] rounded-md px-3 py-2" data-testid={`${testid}-notice`}>
                    {notice}
                </div>
            )}
            {error && (
                <div className="mt-3 text-sm text-[#DA3535]" data-testid={`${testid}-error`}>{error}</div>
            )}

            {confirmOpen && (
                <div
                    className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 px-3"
                    role="dialog"
                    aria-modal="true"
                    data-testid={`${testid}-confirm-modal`}
                >
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                        <div className="p-5 md:p-6">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#E1E6F0] flex items-center justify-center shrink-0">
                                    <LogOut size={18} className="text-[#131B2F]" />
                                </div>
                                <div>
                                    <div className="text-base font-medium text-[#131B2F]">Sign out of every other device?</div>
                                    <p className="mt-1 text-sm text-[#4B5565]">
                                        You'll stay signed in here. Anyone using your account on another laptop, tablet or
                                        phone will need to sign in again next time.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="px-5 md:px-6 py-3 bg-[#F9FAFB] rounded-b-xl flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setConfirmOpen(false)}
                                disabled={busy}
                                data-testid={`${testid}-confirm-cancel`}
                                className="h-10 px-4 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7] disabled:opacity-60"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={performLogoutOthers}
                                disabled={busy}
                                data-testid={`${testid}-confirm-ok`}
                                className="h-10 px-4 rounded-md bg-[#131B2F] hover:bg-[#0B1220] text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60"
                            >
                                {busy && <Loader2 size={14} className="animate-spin" />}
                                Sign out other devices
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
